import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Logo } from './components/Logo'
import { AuthPage } from './pages/AuthPage'
import { api, ApiError, readSession, saveSession } from './lib/api'
import type { AuthSession } from './lib/types'
import { prepareNativePushListeners, unregisterNativePush } from './lib/nativePush'

const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'))

/** 邀请链接深链：/join/<token>（群二维码扫出来就是它）。 */
function readJoinToken(): string {
  const match = /^\/join\/([^/?#]+)/.exec(window.location.pathname)
  if (!match) return ''
  try { return decodeURIComponent(match[1]) } catch { return match[1] }
}

/** 加好友链接深链：/add/<token>（「我的二维码」扫出来就是它）。 */
function readFriendToken(): string {
  const match = /^\/add\/([^/?#]+)/.exec(window.location.pathname)
  if (!match) return ''
  try { return decodeURIComponent(match[1]) } catch { return match[1] }
}

export default function App() {
  const [deletionComplete, setDeletionComplete] = useState(false)
  const [session, setSession] = useState<AuthSession | null>(() => readSession())
  const [joinToken, setJoinToken] = useState(readJoinToken)
  const [friendToken, setFriendToken] = useState(readFriendToken)
  const previousSessionToken = useRef(session?.token)

  useEffect(() => { void prepareNativePushListeners().catch(() => { /* 登记时仍会重试安装 */ }) }, [])

  useEffect(() => {
    const readQrDestination = () => {
      setJoinToken(readJoinToken())
      setFriendToken(readFriendToken())
    }
    window.addEventListener('shixu:open-qr', readQrDestination)
    window.addEventListener('popstate', readQrDestination)
    return () => {
      window.removeEventListener('shixu:open-qr', readQrDestination)
      window.removeEventListener('popstate', readQrDestination)
    }
  }, [])

  useEffect(() => {
    const previous = previousSessionToken.current
    previousSessionToken.current = session?.token
    if (previous && !session) {
      void unregisterNativePush(previous).catch(() => { /* 退出或会话失效后仍清理本机登记 */ })
    }
  }, [session?.token])

  useEffect(() => {
    if (!session) return
    let active = true
    // 已保存的会话可立即显示首页；校验在后台进行，失效时仍按 401 清理并登出。
    api.me(session.token).then(({ user }) => {
      if (!active) return
      const next = { ...session, user }
      setSession(next); saveSession(next)
    }).catch((error) => {
      if (!active) return
      if (error instanceof ApiError && error.status === 401) { saveSession(null); setSession(null) }
    })
    return () => { active = false }
  }, [session?.token])

  // 邀请链接：未登录时照常显示登录页，只在顶部提示登录后会加入群聊。
  if (!session && deletionComplete) return <div className="auth-page"><main className="auth-phone deletion-complete"><Logo /><section className="auth-card" role="status"><h1>账号已注销</h1><p>你的诗序账号及关联的在线数据已删除，所有登录设备已退出。此操作无法恢复。</p><p>感谢你曾与诗序一起读诗。</p><button className="primary-button" onClick={() => setDeletionComplete(false)}>返回登录</button></section></main></div>
  if (!session) return <AuthPage onAuthenticated={setSession} joinHint={Boolean(joinToken) || Boolean(friendToken)} />
  return <Suspense fallback={<div className="splash-screen"><span>诗</span><p>正在打开诗序…</p></div>}>
    <AuthenticatedApp session={session} joinToken={joinToken} onJoinHandled={() => setJoinToken('')} friendToken={friendToken} onFriendHandled={() => setFriendToken('')} onSessionChange={(next, reason) => { setDeletionComplete(reason === 'account-deleted'); setSession(next) }} />
  </Suspense>
}
